import React, { useContext, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Add, Edit, Delete } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { listCategories } from "../../services/ticketExtras";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 980,
    margin: "0 auto",
  },
  actionsBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
}));

const defaultForm = { id: null, name: "", description: "", categoryId: "", active: true };

const Servicos = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterActive, setFilterActive] = useState("all");

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== "all") params.categoryId = filterCategory;
      const { data } = await api.get("/service-catalog", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); }, [filterCategory]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await listCategories();
        setCategories(Array.isArray(data) ? data : (data.items || []));
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const ch = `company-${user.companyId}-service-catalog`;
    const onMsg = (ev) => {
      const { action, service } = ev || {};
      if (!action) return;
      if (action === "create") {
        setRows((prev) => {
          const exists = prev.some((r) => r.id === service.id);
          if (exists) return prev.map((r) => (r.id === service.id ? service : r));
          return [service, ...prev];
        });
      } else if (action === "update") {
        setRows((prev) => prev.map((r) => (r.id === service.id ? service : r)));
      } else if (action === "delete") {
        setRows((prev) => prev.filter((r) => r.id !== service.id));
      }
    };
    socket.on(ch, onMsg);
    return () => socket.off(ch, onMsg);
  }, [socket, user.companyId]);

  const handleOpen = (row) => {
    if (row) setForm({ id: row.id, name: row.name || "", description: row.description || "", categoryId: row.categoryId || "", active: !!row.active });
    else setForm(defaultForm);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleToggleActive = (e) => {
    const { checked } = e.target;
    setForm((p) => ({ ...p, active: checked }));
  };

  const save = async () => {
    try {
      const payload = {
        name: (form.name || "").trim(),
        description: (form.description || "").trim(),
        categoryId: form.categoryId === "" ? null : Number(form.categoryId),
        active: !!form.active,
      };
      if (!payload.name) return toast.error("Informe o nome do serviço");
      if (form.id) await api.put(`/service-catalog/${form.id}`, payload);
      else await api.post(`/service-catalog`, payload);
      setOpen(false);
      await loadRows();
      toast.success("Serviço salvo");
    } catch (e) {
      if (e?.response?.data?.error) toast.error(e.response.data.error);
      else toast.error("Erro ao salvar serviço");
    }
  };

  const remove = async (row) => {
    if (!row?.id) return;
    try {
      await api.delete(`/service-catalog/${row.id}`);
      toast.success("Serviço excluído");
      await loadRows();
    } catch (e) {
      if (e?.response?.data?.error) toast.error(e.response.data.error);
      else toast.error("Não foi possível excluir");
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterActive === "active" && !r.active) return false;
      if (filterActive === "inactive" && r.active) return false;
      return true;
    });
  }, [rows, filterActive]);

  return (
    <div className={classes.root}>
      <div className={classes.actionsBar}>
        <Typography variant="h6">Serviços</Typography>
        <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen(null)}>Novo serviço</Button>
      </div>

      <div className={classes.filters}>
        <TextField
          select
          variant="outlined"
          size="small"
          label="Categoria"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <MenuItem value="all">Todas</MenuItem>
          {(categories || []).map((c) => (<MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>))}
        </TextField>
        <TextField
          select
          variant="outlined"
          size="small"
          label="Status"
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="active">Ativos</MenuItem>
          <MenuItem value="inactive">Inativos</MenuItem>
        </TextField>
      </div>

      <Box mt={2}>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(filteredRows || []).map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{(() => {
                    if (!r.categoryId) return <Chip size="small" label="Global"/>;
                    const c = (categories || []).find((x) => String(x.id) === String(r.categoryId));
                    return c ? (<Chip size="small" label={c.name} style={{ backgroundColor: c.color || "#eceff1", color: "#fff"}} />) : r.categoryId;
                  })()}</TableCell>
                  <TableCell>{r.active ? "Sim" : "Não"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(r)}><Edit /></IconButton>
                    <IconButton size="small" onClick={() => remove(r)}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Editar serviço" : "Novo serviço"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome"
            value={form.name}
            name="name"
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Descrição"
            value={form.description}
            name="description"
            onChange={handleChange}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />
          <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel id="cat-label">Categoria (opcional)</InputLabel>
            <Select
              labelId="cat-label"
              name="categoryId"
              value={form.categoryId}
              onChange={handleChange}
              label="Categoria (opcional)"
            >
              <MenuItem value=""><em>Global</em></MenuItem>
              {(categories || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" mt={1}>
            <Switch checked={!!form.active} onChange={handleToggleActive} color="primary" />
            <Typography>Ativo</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Servicos;
